// Crypto Wallets → Configuration → Network (SM-267, split out of the old
// single /wallet-ops/config page). The chains the crypto rail scans and
// settles on: chain id, confirmations, block time, and scan batch tune the
// pipeline; the RPC endpoint can be probed before saving. Mutations gate on
// CRYPTO_WALLET_CONFIG (server-enforced; the UI also hides the form).
//
// Creation goes through ONE path (SM-276): the "New network" button opens
// the guided onboarding wizard (details → wallets → enable). The inline
// form below the header is EDIT-only — a standalone create form used to
// live here too, but networks made that way skipped system-wallet setup.
import { createFileRoute } from "@tanstack/react-router";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { Card, CardBody, CardHeader, CardTitle } from "@/components/ui/Card";
import { Badge } from "@/components/ui/Badge";
import { Button } from "@/components/ui/Button";
import { Input } from "@/components/ui/Input";
import { Select } from "@/components/ui/Select";
import { Skeleton } from "@/components/ui/Skeleton";
import {
  EnabledToggle,
  errMsg,
  td,
  th,
  thRight,
  useCanConfig,
} from "@/components/wallet-ops/configShared";
import { OnboardChainWizard } from "@/components/wallet-ops/OnboardChainWizard";
import { ConfirmModal } from "@/components/ui/ConfirmModal";
import {
  fetchCryptoNetworks,
  setCryptoNetworkArchived,
  testCryptoNetworkRpc,
  upsertCryptoNetwork,
  walletKeys,
} from "@/lib/wallet-queries";
import type { CryptoNetwork } from "@/lib/wallet-types";

export const Route = createFileRoute("/_app/wallet-ops/config/network")({
  component: NetworkConfigPage,
});

function NetworkConfigPage() {
  const canConfig = useCanConfig();
  const [showArchived, setShowArchived] = useState(false);
  const networks = useQuery({
    queryKey: walletKeys.networksList(showArchived),
    queryFn: () => fetchCryptoNetworks(showArchived),
  });

  return (
    <div className="space-y-6">
      <div>
        <h1 className="font-display text-2xl tracking-tight">Networks</h1>
        <p className="text-sm text-muted">
          The chains the crypto rail scans and settles on. Confirmations, block
          time, and scan batch tune the pipeline; test an RPC endpoint before
          saving it.
        </p>
      </div>
      <NetworksSection
        networks={networks.data ?? []}
        loading={networks.isLoading}
        canConfig={canConfig}
        showArchived={showArchived}
        onShowArchived={setShowArchived}
      />
    </div>
  );
}

function NetworksSection({
  networks,
  loading,
  canConfig,
  showArchived,
  onShowArchived,
}: {
  networks: CryptoNetwork[];
  loading: boolean;
  canConfig: boolean;
  showArchived: boolean;
  onShowArchived: (v: boolean) => void;
}) {
  const qc = useQueryClient();
  // form is EDIT-only (set from a row's Edit action) — creation always goes
  // through the onboarding wizard so no network skips system-wallet setup.
  const [form, setForm] = useState<CryptoNetwork | null>(null);
  const [wizardOpen, setWizardOpen] = useState(false);
  const [archiveTarget, setArchiveTarget] = useState<CryptoNetwork | null>(null);

  const archive = useMutation({
    mutationFn: ({ n, reason }: { n: CryptoNetwork; reason: string }) =>
      setCryptoNetworkArchived(n.network_id, !n.archived, reason),
    onSuccess: async () => {
      setArchiveTarget(null);
      await qc.invalidateQueries({ queryKey: walletKeys.networks() });
    },
  });
  const save = useMutation({
    mutationFn: (input: CryptoNetwork) => upsertCryptoNetwork(input),
    onSuccess: async () => {
      setForm(null);
      await qc.invalidateQueries({ queryKey: walletKeys.networks() });
    },
    meta: { successMessage: "Network saved." },
  });
  const testRpc = useMutation({
    mutationFn: (n: CryptoNetwork) => testCryptoNetworkRpc(n.rpc_url, n.chain_id),
  });

  const set = <K extends keyof CryptoNetwork>(k: K, v: CryptoNetwork[K]) =>
    setForm((f) => (f ? { ...f, [k]: v } : f));
  const closeEdit = () => {
    setForm(null);
    save.reset();
    testRpc.reset();
  };

  return (
    <Card>
      <CardHeader className="border-b border-border">
        <CardTitle>Networks</CardTitle>
        <div className="flex items-center gap-3">
          <label className="flex cursor-pointer items-center gap-1.5 text-2xs text-muted">
            <input
              type="checkbox"
              checked={showArchived}
              onChange={(e) => onShowArchived(e.target.checked)}
            />
            Show archived
          </label>
          {canConfig && (
            <Button size="sm" onClick={() => setWizardOpen(true)}>
              New network
            </Button>
          )}
        </div>
      </CardHeader>
      <OnboardChainWizard open={wizardOpen} onClose={() => setWizardOpen(false)} />
      <ConfirmModal
        open={!!archiveTarget}
        title={
          archiveTarget?.archived
            ? `Restore ${archiveTarget?.name}?`
            : `Archive ${archiveTarget?.name}?`
        }
        description={
          archiveTarget?.archived ? (
            <>
              The network comes back <strong>disabled</strong> — enable it
              separately once you've checked its config. Restoring fails if
              another live network has taken chain id {archiveTarget?.chain_id}{" "}
              in the meantime.
            </>
          ) : (
            <>
              Stops the scanner, hides the chain from users, and pauses gas
              top-ups and sweeps. Deposits and withdrawals already recorded are
              kept, and nothing is deleted — you can restore it later. Chain id{" "}
              {archiveTarget?.chain_id} is released, so a corrected network can
              use it.
            </>
          )
        }
        confirmLabel={archiveTarget?.archived ? "Restore" : "Archive"}
        tone={archiveTarget?.archived ? "primary" : "danger"}
        reasonLabel="Reason"
        reasonPlaceholder="why this chain is being retired"
        reasonRequired
        loading={archive.isPending}
        error={archive.isError ? errMsg(archive.error) : null}
        onConfirm={(reason) =>
          archiveTarget && archive.mutate({ n: archiveTarget, reason })
        }
        onCancel={() => {
          setArchiveTarget(null);
          archive.reset();
        }}
      />
      <CardBody className="p-0">
        {form && (
          <form
            className="space-y-4 border-b border-border p-5"
            onSubmit={(e) => {
              e.preventDefault();
              save.mutate(form);
            }}
          >
            <p className="text-2xs text-muted">
              Editing <span className="font-semibold text-t-1">{form.name}</span>
            </p>
            <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
              <Input
                label="Name"
                value={form.name}
                onChange={(e) => set("name", e.target.value)}
                placeholder="Ethereum Sepolia"
                required
              />
              <Select
                label="Standard"
                value={form.standard}
                onChange={(e) => set("standard", e.target.value)}
                options={[{ value: "EIP155", label: "EIP155" }]}
              />
              <Input
                label="Chain ID"
                value={String(form.chain_id)}
                onChange={(e) => set("chain_id", Number(e.target.value) || 0)}
                inputMode="numeric"
              />
              <Input
                label="Confirmations"
                value={String(form.confirmations)}
                onChange={(e) =>
                  set("confirmations", Number(e.target.value) || 0)
                }
                inputMode="numeric"
              />
              <Input
                label="Block time (ms)"
                value={String(form.block_time_ms)}
                onChange={(e) =>
                  set("block_time_ms", Number(e.target.value) || 0)
                }
                inputMode="numeric"
              />
              <Input
                label="Scan batch"
                value={String(form.scan_batch)}
                onChange={(e) => set("scan_batch", Number(e.target.value) || 0)}
                inputMode="numeric"
              />
              <EnabledToggle
                checked={form.enabled}
                onChange={(v) => set("enabled", v)}
              />
            </div>
            <div className="space-y-1.5">
              <div className="flex items-end gap-2">
                <div className="min-w-0 flex-1">
                  <Input
                    label="RPC endpoint"
                    value={form.rpc_url}
                    onChange={(e) => {
                      set("rpc_url", e.target.value);
                      testRpc.reset();
                    }}
                    placeholder="https://rpc-testnet.example.com  (blank = use service env)"
                    spellCheck={false}
                    className="font-mono"
                  />
                </div>
                <Button
                  type="button"
                  variant="secondary"
                  loading={testRpc.isPending}
                  disabled={!form.rpc_url}
                  onClick={() => testRpc.mutate(form)}
                >
                  Test connection
                </Button>
              </div>
              <p className="text-2xs text-muted">
                DB value wins over the service env (RPC_&lt;NET&gt;). Leave blank
                for keyed provider URLs that must stay in secrets.
              </p>
              {testRpc.isSuccess &&
                (testRpc.data.ok ? (
                  <p
                    className={`text-2xs ${testRpc.data.matches ? "text-yes" : "text-no"}`}
                  >
                    {testRpc.data.matches
                      ? `✓ Reachable — chain id ${testRpc.data.reported_chain_id} matches`
                      : `⚠ Reachable but chain id ${testRpc.data.reported_chain_id} ≠ expected ${testRpc.data.expected_chain_id}`}
                  </p>
                ) : (
                  <p className="text-2xs text-no">
                    ✕ Unreachable: {testRpc.data.error}
                  </p>
                ))}
            </div>
            {save.isError && (
              <p className="text-2xs text-no">{errMsg(save.error)}</p>
            )}
            <div className="flex items-center gap-2">
              <Button type="submit" loading={save.isPending}>
                Save network
              </Button>
              <Button type="button" variant="ghost" onClick={closeEdit}>
                Cancel
              </Button>
            </div>
          </form>
        )}
        {loading && (
          <div className="space-y-2 p-5">
            {[0, 1].map((i) => (
              <Skeleton key={i} className="h-10" />
            ))}
          </div>
        )}
        {!loading && networks.length === 0 && (
          <p className="px-5 py-10 text-center text-sm text-muted">
            No networks configured yet.
          </p>
        )}
        {networks.length > 0 && (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="border-b border-border bg-surface-2">
                <tr>
                  <th className={`${th} px-5`}>Network</th>
                  <th className={th}>Standard</th>
                  <th className={thRight}>Chain ID</th>
                  <th className={thRight}>Confirmations</th>
                  <th className={thRight}>Block time</th>
                  <th className={thRight}>Scan batch</th>
                  <th className={th}>Enabled</th>
                  {canConfig && <th className={thRight} />}
                </tr>
              </thead>
              <tbody>
                {networks.map((n) => (
                  <tr
                    key={n.network_id}
                    className="border-b border-border/60 last:border-0"
                  >
                    <td className={`${td} px-5 font-medium text-fg`} title={n.network_id}>
                      {n.name}
                    </td>
                    <td className={td}>{n.standard}</td>
                    <td className={`${td} num text-right`}>{n.chain_id}</td>
                    <td className={`${td} num text-right`}>{n.confirmations}</td>
                    <td className={`${td} num text-right`}>{n.block_time_ms} ms</td>
                    <td className={`${td} num text-right`}>{n.scan_batch}</td>
                    <td className={td}>
                      {n.archived ? (
                        <Badge tone="muted">Archived</Badge>
                      ) : (
                        <Badge tone={n.enabled ? "success" : "muted"}>
                          {n.enabled ? "Enabled" : "Disabled"}
                        </Badge>
                      )}
                    </td>
                    {canConfig && (
                      <td className={`${td} text-right`}>
                        <div className="flex justify-end gap-1">
                          {!n.archived && (
                            <Button
                              size="sm"
                              variant="ghost"
                              onClick={() => setForm({ ...n })}
                            >
                              Edit
                            </Button>
                          )}
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() => setArchiveTarget(n)}
                          >
                            {n.archived ? "Restore" : "Archive"}
                          </Button>
                        </div>
                      </td>
                    )}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </CardBody>
    </Card>
  );
}
